import { useEffect, useMemo, useState } from 'react'
import { AddressElement } from '@stripe/react-stripe-js'
import {
  convertAddressToStripeFormat,
  createAddressFromStripeFormat,
  getPhoneFieldOptions,
  type StripeAddress,
} from './stripeAdapter'
import { COUNTRY_CODES } from '../../config/countries-data'
import { googleMapsAPI } from '../../services/googleMaps'
import * as S from './StripeAddressForm.styles'
import StripeInput from '../StripeInput/StripeInput'
import Checkbox from 'mixtiles-web-common/ui/Checkbox'
import { translateManager as t } from '../../services/TranslateManager'
import { experimentManager } from '../../services/ExperimentManager/ExperimentManager'
import { useAccount } from '../Account/AccountProvider'
import { getUserEmail } from '../../services/userEmail'
import {
  type AddressMode,
  type StripeAddressElementChangeEvent,
  type StripeAddressElementOptions,
  type StripeError,
} from '@stripe/stripe-js'
import { type Address } from '../Pricing/types/checkout.types'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { isSmsForbiddenByNumber } from '../../hooks/useSmsForbidden'

type Props = {
  mode: AddressMode
  address: Address
  onChange: (address: Address, complete: boolean) => void
  onLoadError?: (event: { elementType: 'address'; error: StripeError }) => void
  shouldCapturePhone?: boolean
  emailInputParams: {
    value: string
    onChange: (newValue: string) => void
    isInvalid: boolean
    onBlur: () => void
  }
  emailConsentParams?: {
    checked: boolean
    onChange: (newValue: boolean) => void
  }
  smsConsentParams?: {
    checked: boolean
    onChange: (newValue: boolean) => void
  }
  fullNameKanaParams?: {
    show: boolean
    value: string
    onChange: (newValue: string) => void
    isInvalid: boolean
  }
}

function StripeAddressForm({
  mode,
  address,
  onChange,
  onLoadError,
  shouldCapturePhone,
  emailInputParams,
  emailConsentParams,
  smsConsentParams,
  fullNameKanaParams,
}: Props) {
  const { isAuthenticated } = useAccount()
  const [isAddressElementLoaded, setIsAddressElementLoaded] =
    useState<boolean>(false)
  const defaultValues = useMemo(
    () => convertAddressToStripeFormat(address || {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const isSmsConsentEnabled = experimentManager.isEnabled('sms-consent')
  const isPhoneFieldShown = shouldCapturePhone || isSmsConsentEnabled
  // A workaround to check whether the AddressElement is fully shown, because Stripe
  // don't support the "collapsed" parameter in onChange event for AddressElement (they only it for PaymentElement)
  const [isAddressFullyShown, setIsAddressFullyShown] = useState<boolean>(
    address?.city?.length > 0
  )

  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const isSmsForbidden = isSmsForbiddenByNumber(phoneNumber)

  useEffect(() => {
    if (isSmsForbidden) {
      smsConsentParams?.onChange(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmsForbidden])

  const phoneFieldOptions = getPhoneFieldOptions({
    isPhoneFieldShown,
    isPhoneRequired: shouldCapturePhone,
  })

  const options: StripeAddressElementOptions = {
    mode,
    allowedCountries: COUNTRY_CODES,
    autocomplete: {
      mode: 'google_maps_api',
      apiKey: googleMapsAPI.getApiKey(),
    },
    defaultValues,
    ...phoneFieldOptions,
  }

  const handleOnReady = () => {
    setIsAddressElementLoaded(true)
    analytics.track(EVENT_NAMES.ADDRESS_ELEMENT_LOADED, {
      Mode: mode,
    })
  }

  const handleLoadError = (event: {
    elementType: 'address'
    error: StripeError
  }) => {
    const { error } = event
    analytics.track(EVENT_NAMES.ADDRESS_ELEMENT_LOAD_ERROR, {
      Mode: mode,
      errorType: error.type,
      errorCode: error.code,
      errorMessage: error.message,
    })
    onLoadError?.(event)
  }

  const handleAddressChange = (event: StripeAddressElementChangeEvent) => {
    const { value, complete } = event
    // When mounting the isAddressLoaded is false, but it is actually loaded and we are missing the
    // on change event that will set the address to complete
    if (isAddressElementLoaded || (value && complete)) {
      const address = createAddressFromStripeFormat(value as StripeAddress)
      onChange(address, complete)
      setPhoneNumber(address.phoneNumber || '')

      if (address?.city?.length > 0 && !isAddressFullyShown) {
        setIsAddressFullyShown(true)
      }
    }
  }

  return (
    <S.Container
      $noPadding={isAddressFullyShown}
      data-testid={
        isAddressElementLoaded ? 'stripe-address-element-loaded' : null
      }
    >
      {isAddressElementLoaded &&
        emailInputParams &&
        (!isAuthenticated || !getUserEmail()) && (
          <S.ExtraFieldWrapper>
            <StripeInput
              {...emailInputParams}
              placeholder={t.get('get_started.form_fields.email')}
              label={t.get('get_started.form_fields.email')}
              name="email"
              testId="address-email"
            />
            {mode === 'shipping' && emailConsentParams && (
              <S.EmailConsentContainer>
                <Checkbox {...emailConsentParams} />
                <S.ConsentText>
                  {t.get('general.address_form.suggest_offers_email')}
                </S.ConsentText>
              </S.EmailConsentContainer>
            )}
          </S.ExtraFieldWrapper>
        )}
      {isAddressElementLoaded && fullNameKanaParams?.show && (
        <S.ExtraFieldWrapper>
          <StripeInput
            {...fullNameKanaParams}
            placeholder={t.get(
              'general.address_form.form_fields.full_name_kana_placeholder'
            )}
            label={t.get('general.address_form.form_fields.full_name_kana')}
            name="fullNameKana"
            testId="address-full-name-kana"
            onBlur={() => undefined}
          />
        </S.ExtraFieldWrapper>
      )}
      <AddressElement
        options={options}
        onChange={(e) => handleAddressChange(e)}
        onReady={handleOnReady}
        onLoadError={handleLoadError}
        className="stripe-address-element"
      />
      {isPhoneFieldShown && isAddressFullyShown && (
        <S.PhoneNumberDisclaimer>
          {t.get('general.address_form.phone_number_info')}
        </S.PhoneNumberDisclaimer>
      )}
      {isAddressElementLoaded &&
        mode === 'shipping' &&
        isSmsConsentEnabled &&
        smsConsentParams &&
        isAddressFullyShown &&
        !isSmsForbidden && (
          <S.SmsConsentContainer>
            <Checkbox {...smsConsentParams} />
            <S.ConsentText>
              {t.get('general.address_form.suggest_offers')}
            </S.ConsentText>
          </S.SmsConsentContainer>
        )}
    </S.Container>
  )
}

export default StripeAddressForm
